import { useState, useRef, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { useMapEvents } from 'react-leaflet/hooks';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Button from './ui/Button';
import { reverseGeocode, searchAddress } from '../utils/geocode';

const DEFAULT_CENTER = [25.2048, 55.2708]; // Dubai
const DEFAULT_ZOOM = 7;
const PIN_ZOOM = 15;

const pinIcon = L.divIcon({
  className: '',
  html: '<div class="w-7 h-7 rounded-full rounded-bl-none bg-terra-400 border-2 border-white shadow-hover -rotate-45"></div>',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng);
    },
  });
  return null;
}

export default function MapAddressPicker({
  value,
  onChange,
  onResolvedAddress,
  height = '280px',
  className = '',
}) {
  const mapRef = useRef(null);
  const searchAbortRef = useRef(null);
  const reverseAbortRef = useRef(null);
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState('');

  const resolvePosition = useCallback(async (lat, lng) => {
    if (!mountedRef.current) return;
    onChange({ latitude: lat, longitude: lng });

    reverseAbortRef.current?.abort();
    const controller = new AbortController();
    reverseAbortRef.current = controller;

    try {
      const resolved = await reverseGeocode(lat, lng, { signal: controller.signal });
      if (mountedRef.current) onResolvedAddress?.(resolved);
    } catch {
      // Reverse geocoding is best-effort — user can still fill fields manually.
    }
  }, [onChange, onResolvedAddress]);

  const handlePick = useCallback((latlng) => {
    resolvePosition(latlng.lat, latlng.lng);
  }, [resolvePosition]);

  const handleDragEnd = useCallback((e) => {
    const { lat, lng } = e.target.getLatLng();
    resolvePosition(lat, lng);
  }, [resolvePosition]);

  const handleLocate = () => {
    if (!navigator.geolocation) {
      setLocateError('Geolocation not supported on this device.');
      return;
    }
    setLocating(true);
    setLocateError('');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (!mountedRef.current) return;
        resolvePosition(coords.latitude, coords.longitude);
        mapRef.current?.flyTo([coords.latitude, coords.longitude], PIN_ZOOM);
        setLocating(false);
      },
      (err) => {
        if (!mountedRef.current) return;
        setLocateError(err.code === 1 ? 'Location access denied.' : 'Could not detect location.');
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleQueryChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    setShowResults(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    searchAbortRef.current?.abort();

    if (next.trim().length < 3) {
      setResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      searchAbortRef.current = controller;
      try {
        const found = await searchAddress(next, { signal: controller.signal });
        setResults(found);
      } catch {
        // Stale/aborted request — a newer one is already in flight.
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const handleSelectResult = (result) => {
    setQuery(result.displayName);
    setShowResults(false);
    setResults([]);
    resolvePosition(result.latitude, result.longitude);
    mapRef.current?.flyTo([result.latitude, result.longitude], PIN_ZOOM);
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      searchAbortRef.current?.abort();
      reverseAbortRef.current?.abort();
    };
  }, []);

  const initialCenter = value ? [value.latitude, value.longitude] : DEFAULT_CENTER;
  const initialZoom = value ? PIN_ZOOM : DEFAULT_ZOOM;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => results.length > 0 && setShowResults(true)}
            placeholder="Search for an address…"
            className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 placeholder:text-ink-400 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
          />
          {showResults && (results.length > 0 || searching) && (
            <div className="absolute z-[1000] top-full mt-1 w-full bg-white border border-sand-200 rounded-md shadow-card overflow-hidden">
              {searching && (
                <div className="px-4 py-2.5 text-sm text-ink-400">Searching…</div>
              )}
              {!searching && results.map((result, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => handleSelectResult(result)}
                  className="w-full text-left px-4 py-2.5 text-sm text-ink-900 hover:bg-sand-100 transition-colors duration-150 border-b border-sand-100 last:border-b-0"
                >
                  {result.displayName}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={handleLocate} disabled={locating}>
          {locating ? 'Locating…' : 'Use my location'}
        </Button>
      </div>

      {locateError && <p className="text-xs text-uae-red">{locateError}</p>}

      <div className="rounded-lg border border-sand-200 shadow-card overflow-hidden" style={{ height }}>
        <MapContainer
          ref={mapRef}
          center={initialCenter}
          zoom={initialZoom}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom
          inertia={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={handlePick} />
          {value && (
            <Marker
              position={[value.latitude, value.longitude]}
              icon={pinIcon}
              draggable
              eventHandlers={{ dragend: handleDragEnd }}
            />
          )}
        </MapContainer>
      </div>

      <p className="text-xs text-ink-400">Tap the map or drag the pin to set the exact delivery location.</p>
    </div>
  );
}
